import { Box, Paper, Typography, useTheme } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const SolarROIChart = ({ roiData, totalInstallationCost }) => {
  const theme = useTheme();

  // Format data for chart
  const chartData =
    roiData?.map((point) => ({
      year: point.year,
      "درآمد تجمعی": Math.round(point.cumulativeIncome / 1000000), // Convert to millions
      "هزینه تجمعی": Math.round(point.cumulativeCost / 1000000), // Convert to millions
      "سود خالص": Math.round(point.netProfit / 1000000), // Convert to millions
    })) || [];

  // Find break-even point
  const breakEvenPoint = roiData?.find((point) => point.netProfit >= 0);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          sx={{
            p: 2,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            سال {label}
          </Typography>
          {payload.map((item, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: item.color, display: "flex", alignItems: "center" }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: item.color,
                  borderRadius: "50%",
                  mr: 1,
                }}
              />
              {item.name}: {new Intl.NumberFormat("fa-IR").format(item.value)}{" "}
              میلیون ریال
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // Custom label formatter for axes
  const formatYAxisLabel = (value) => {
    return new Intl.NumberFormat("fa-IR").format(value);
  };

  const formatXAxisLabel = (value) => {
    return `سال ${value}`;
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ textAlign: "center", mb: 3 }}>
        نمودار بازگشت سرمایه (ROI) طی 25 سال
      </Typography>

      {breakEvenPoint && (
        <Box sx={{ mb: 2, p: 2, bgcolor: "success.light", borderRadius: 1 }}>
          <Typography variant="body2" color="success.dark">
            <strong>نقطه سر به سر:</strong> سال {breakEvenPoint.year} - پس از{" "}
            {breakEvenPoint.year} سال سرمایه شما بازگشت خواهد کرد
          </Typography>
        </Box>
      )}

      <Box sx={{ width: "100%", height: 400, direction: "ltr" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 40,
              bottom: 20,
            }}
          >
            <defs>
              <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={theme.palette.success.main}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={theme.palette.success.main}
                  stopOpacity={0.1}
                />
              </linearGradient>
              <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={theme.palette.error.main}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={theme.palette.error.main}
                  stopOpacity={0.1}
                />
              </linearGradient>
              <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={theme.palette.primary.main}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={theme.palette.primary.main}
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.palette.divider}
            />

            <XAxis
              dataKey="year"
              tickFormatter={formatXAxisLabel}
              stroke={theme.palette.text.secondary}
            />

            <YAxis
              tickFormatter={formatYAxisLabel}
              label={{
                value: "میلیون ریال",
                angle: -90,
                position: "insideLeft",
                style: {
                  textAnchor: "middle",
                  fill: theme.palette.text.secondary,
                },
              }}
              stroke={theme.palette.text.secondary}
            />

            <Tooltip content={<CustomTooltip />} />

            <Legend
              wrapperStyle={{
                paddingTop: "20px",
                direction: "rtl",
                textAlign: "right",
              }}
            />

            <Area
              type="monotone"
              dataKey="هزینه تجمعی"
              stackId="1"
              stroke={theme.palette.error.main}
              fill="url(#costGradient)"
              strokeWidth={2}
            />

            <Area
              type="monotone"
              dataKey="درآمد تجمعی"
              stackId="2"
              stroke={theme.palette.success.main}
              fill="url(#incomeGradient)"
              strokeWidth={2}
            />

            <Line
              type="monotone"
              dataKey="سود خالص"
              stroke={theme.palette.primary.main}
              strokeWidth={3}
              dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
              activeDot={{
                r: 6,
                stroke: theme.palette.primary.main,
                strokeWidth: 2,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>راهنما:</strong> نمودار بالا نشان‌دهنده تجمیع درآمد و
          هزینه‌های شما طی 25 سال است. خط آبی (سود خالص) زمانی که از صفر عبور
          کند، نقطه بازگشت سرمایه شماست. تمام محاسبات با در نظر گیری تورم 20%
          سالانه و کاهش 0.5% سالانه بازده پنل‌ها انجام شده است.
        </Typography>
      </Box>
    </Paper>
  );
};

export default SolarROIChart;
